import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
	ArrowLeft,
	Minus,
	Plus,
	ShoppingBag,
	Eye,
	Users,
	Leaf,
	MapPin,
	Clock,
	Award,
	Shield,
	Flame,
	Check,
} from "lucide-react";
import { Helmet } from "react-helmet-async";
import { Button } from "../components/ui/button";
import { ProductCard } from "../components/products/ProductCard";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";
import { supabase } from "../lib/supabase";
import api from "../lib/api";
import { trackEvent, toGaItem } from "../lib/analyticsEvents";

// ── Mapping unique catégorie slug → label ─────────────────────────
const CATEGORY_LABELS = {
	viennoiserie: "Viennoiserie",
	sandwich: "Sandwich",
	snacks: "Snack",
	cafes: "Café",
	"boissons-froides": "Boisson froide",
	dessert: "Dessert",
};

// ── Données contextuelles par catégorie ───────────────────────────
const CATEGORY_INFO = {
	viennoiserie: {
		origin: {
			icon: MapPin,
			label: "Provenance",
			value: "Artisans genevois",
			detail:
				"Viennoiseries préparées par nos artisans partenaires à Genève, avec du beurre AOP et de la farine suisse.",
		},
		process: {
			icon: Clock,
			label: "Fraîcheur",
			value: "Livrées chaque matin",
			detail:
				"Nos viennoiseries arrivent fraîches chaque matin avant l'ouverture. Rien n'est préparé la veille.",
		},
		badges: ["Fraîches du matin", "Beurre AOP", "Artisanales"],
		story:
			"Nos viennoiseries sont une déclaration d'amour au goût authentique. Croissants feuilletés, brioches moelleuses, roulés généreux — chaque création est pensée pour transformer votre petit-déjeuner en petit moment de plaisir.",
		assurance: [
			{ icon: Clock, text: "Livrées chaque matin" },
			{ icon: Award, text: "Recettes traditionnelles" },
			{ icon: Shield, text: "Sans additifs" },
		],
	},
	sandwich: {
		origin: {
			icon: Leaf,
			label: "Ingrédients",
			value: "Frais & locaux",
			detail:
				"Pain artisanal, légumes du marché et produits sélectionnés auprès de producteurs suisses de confiance.",
		},
		process: {
			icon: Clock,
			label: "Préparation",
			value: "Assemblés chaque jour",
			detail:
				"Nos sandwiches sont préparés chaque matin. Le pain est cuit sur place, les garnitures assemblées à la commande dès que possible.",
		},
		badges: ["Pain artisanal", "Ingrédients frais", "Produits suisses"],
		story:
			"Un bon sandwich, c'est d'abord un bon pain. Le nôtre est fait maison : torsade, foccacia, pain au lait, paillasse. On construit ensuite chaque recette avec la même exigence — des produits frais et un vrai équilibre des saveurs.",
		assurance: [
			{ icon: Shield, text: "Préparé chaque jour" },
			{ icon: Leaf, text: "Produits locaux" },
			{ icon: Award, text: "Pain fait maison" },
		],
	},
	snacks: {
		origin: {
			icon: Leaf,
			label: "Ingrédients",
			value: "Frais & locaux",
			detail:
				"Légumes frais, pâtes maison et produits sélectionnés auprès de producteurs locaux suisses.",
		},
		process: {
			icon: Clock,
			label: "Préparation",
			value: "Assemblé chaque jour",
			detail:
				"Nos snacks sont assemblés chaque matin avec des ingrédients livrés frais. Rien ne reste plus d'un jour.",
		},
		badges: ["Frais du jour", "Produits locaux", "Fait maison"],
		story:
			"Nos snacks sont conçus pour les moments où l'on veut manger bien sans perdre de temps. Des associations de saveurs soignées, des ingrédients frais et locaux — un vrai repas, pas un en-cas quelconque.",
		assurance: [
			{ icon: Shield, text: "Préparé chaque matin" },
			{ icon: Leaf, text: "Ingrédients frais" },
			{ icon: Award, text: "Recettes maison" },
		],
	},
	cafes: {
		origin: {
			icon: MapPin,
			label: "Origine",
			value: "Éthiopie & Colombie",
			detail:
				"Grains arabica sélectionnés auprès de producteurs certifiés, torréfiés localement à Genève dans les 48h.",
		},
		process: {
			icon: Flame,
			label: "Torréfaction",
			value: "Artisanale à Genève",
			detail:
				"Torréfaction lente à basse température pour révéler toute la complexité aromatique du grain.",
		},
		badges: ["100% Arabica", "Torréfié localement", "Single origin"],
		story:
			"Chaque tasse est le résultat d'un voyage de plusieurs milliers de kilomètres — de la plantation aux mains de nos baristas. Nous travaillons directement avec des coopératives de producteurs pour garantir une traçabilité totale et un commerce équitable.",
		assurance: [
			{ icon: Shield, text: "Préparé à la commande" },
			{ icon: Award, text: "Baristas certifiés SCA" },
			{ icon: Leaf, text: "Commerce équitable" },
		],
	},
	"boissons-froides": {
		origin: {
			icon: Leaf,
			label: "Ingrédients",
			value: "Naturels & frais",
			detail:
				"Sirops maison préparés chaque matin, lait frais livré quotidiennement par un producteur local suisse.",
		},
		process: {
			icon: Clock,
			label: "Préparation",
			value: "À la commande",
			detail:
				"Chaque boisson est assemblée au moment de votre commande pour garantir fraîcheur et qualité optimales.",
		},
		badges: ["Sans conservateurs", "Lait frais local", "Sirops maison"],
		story:
			"Nos boissons froides sont pensées pour allier fraîcheur et plaisir. Chaque recette est développée par notre équipe pour offrir équilibre des saveurs et plaisir visuel — parce qu'on boit aussi avec les yeux.",
		assurance: [
			{ icon: Shield, text: "Préparé à la commande" },
			{ icon: Leaf, text: "Ingrédients naturels" },
			{ icon: Award, text: "Recettes maison" },
		],
	},
	dessert: {
		origin: {
			icon: MapPin,
			label: "Provenance",
			value: "Fait maison à Genève",
			detail:
				"Préparés dans notre laboratoire genevois avec des fruits de saison, du chocolat grand cru et des œufs fermiers.",
		},
		process: {
			icon: Flame,
			label: "Fabrication",
			value: "Artisanale, sans additifs",
			detail:
				"Pas de conservateurs, pas de colorants artificiels. Juste des ingrédients de qualité et un savoir-faire transmis de génération en génération.",
		},
		badges: ["Fait maison", "Sans additifs", "Chocolat grand cru"],
		story:
			"Nos desserts sont une déclaration d'amour au goût authentique. Chaque création est pensée pour surprendre et ravir — des textures contrastées, des saveurs équilibrées, une présentation soignée qui fait de chaque bouchée un moment d'exception.",
		assurance: [
			{ icon: Shield, text: "Sans conservateurs" },
			{ icon: Leaf, text: "Fruits de saison" },
			{ icon: Award, text: "Chocolat grand cru" },
		],
	},
};

// ── Composants d'affichage ────────────────────────────────────────
const Badges = ({ badges }) => (
	<div className="flex flex-wrap gap-2 mb-8">
		{badges.map((badge) => (
			<span
				key={badge}
				className="px-3 py-1 text-xs font-medium uppercase tracking-wider bg-latte-gold/10 text-latte-gold border border-latte-gold/20"
			>
				{badge}
			</span>
		))}
	</div>
);

const InfoBlocks = ({ origin, process }) => (
	<div className="grid grid-cols-2 gap-4 mb-8">
		{[origin, process].map((info) => (
			<div
				key={info.label}
				className="bg-warm-secondary p-4 border border-border-subtle"
			>
				<div className="flex items-center gap-2 mb-2">
					<info.icon className="w-4 h-4 text-latte-gold flex-shrink-0" />
					<span className="text-xs uppercase tracking-wider text-soft-charcoal-secondary font-medium">
						{info.label}
					</span>
				</div>
				<p className="font-serif font-semibold text-espresso text-sm mb-1">
					{info.value}
				</p>
				<p className="text-xs text-soft-charcoal-secondary leading-relaxed">
					{info.detail}
				</p>
			</div>
		))}
	</div>
);

const StoryBlock = ({ story }) => (
	<div className="border-l-2 border-latte-gold pl-4 mb-8">
		<p className="text-soft-charcoal-secondary leading-relaxed text-sm italic">
			{story}
		</p>
	</div>
);

const Assurances = ({ items }) => (
	<div className="flex flex-wrap gap-4 mb-8">
		{items.map((item) => (
			<div
				key={item.text}
				className="flex items-center gap-2 text-sm text-soft-charcoal-secondary"
			>
				<item.icon className="w-4 h-4 text-latte-gold flex-shrink-0" />
				<span>{item.text}</span>
			</div>
		))}
	</div>
);

// ── Sélecteur de variantes (radio buttons customs en grille) ──────
const VariantSelector = ({
	variants,
	selectedVariant,
	onSelect,
	formatPrice,
}) => {
	if (!variants?.length) return null;

	return (
		<div className="mb-6">
			<div className="flex items-center justify-between mb-3">
				<label className="text-sm uppercase tracking-widest text-soft-charcoal-secondary font-medium">
					Choisissez votre version
				</label>
				<span className="text-xs text-soft-charcoal-secondary">
					{variants.length} options
				</span>
			</div>
			<div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
				{variants.map((v) => {
					const isSelected = selectedVariant?.id === v.id;
					return (
						<button
							key={v.id}
							type="button"
							onClick={() => onSelect(v)}
							data-testid={`variant-${v.id}`}
							disabled={v.available === false}
							className={`text-left p-4 border transition-all duration-200 relative ${
								isSelected
									? "border-latte-gold bg-latte-gold/5 shadow-sm"
									: "border-border-subtle hover:border-latte-gold/50 hover:bg-warm-secondary/50"
							} ${v.available === false ? "opacity-40 cursor-not-allowed" : ""}`}
						>
							{isSelected && (
								<Check className="absolute top-3 right-3 w-4 h-4 text-latte-gold" />
							)}
							<div className="flex justify-between items-start gap-2 mb-1">
								<span className="font-medium text-espresso pr-6">{v.name}</span>
								<span className="text-latte-gold font-serif whitespace-nowrap">
									{formatPrice(v.price)}
								</span>
							</div>
							{v.description && (
								<p className="text-xs text-soft-charcoal-secondary leading-relaxed">
									{v.description}
								</p>
							)}
							{v.available === false && (
								<p className="text-xs text-red-500 mt-2 font-medium">
									Indisponible
								</p>
							)}
						</button>
					);
				})}
			</div>
		</div>
	);
};

// ── Composant principal ───────────────────────────────────────────
export default function ProductPage() {
	const { slug } = useParams();
	const navigate = useNavigate();
	const { addItem } = useCart();

	const [quantity, setQuantity] = useState(1);
	const [product, setProduct] = useState(null);
	const [selectedVariant, setSelectedVariant] = useState(null);
	const [selectedImage, setSelectedImage] = useState(null);
	const [relatedProducts, setRelatedProducts] = useState([]);
	const [liveVisitors, setLiveVisitors] = useState(0);
	const [loading, setLoading] = useState(true);
	const [notFound, setNotFound] = useState(false);

	const channelRef = useRef(null);

	const hasVariants = product?.has_variants === true;
	const variants = product?.variants || [];

	const displayedImages = useMemo(() => {
		if (selectedVariant?.images?.length > 0) {
			return selectedVariant.images;
		}

		if (selectedVariant?.image_url) {
			return [
				{
					id: `variant-${selectedVariant.id}`,
					image_url: selectedVariant.image_url,
				},
			];
		}

		return product?.images || [];
	}, [product?.images, selectedVariant]);

	// ── Défilement automatique des images toutes les 2 secondes ──
	useEffect(() => {
		if (displayedImages.length <= 1) return;

		const interval = setInterval(() => {
			setSelectedImage((currentImage) => {
				const currentIndex = displayedImages.findIndex(
					(image) => image.image_url === currentImage,
				);

				const nextIndex = (currentIndex + 1) % displayedImages.length;

				return displayedImages[nextIndex].image_url;
			});
		}, 2000);

		return () => clearInterval(interval);
	}, [displayedImages]);

	const needsVariantSelection = hasVariants && !selectedVariant;

	// Prix affiché : celui de la variante sélectionnée, sinon celui du parent
	const displayedPrice = selectedVariant?.price ?? product?.price ?? 0;
	// Stock effectif : idem
	const effectiveStock =
		selectedVariant?.stock !== undefined
			? selectedVariant.stock
			: typeof product?.stock === "number"
				? product.stock
				: 100;

	useEffect(() => {
		if (!product) return;
		trackEvent("view_item", {
			currency: "CHF",
			value: Number(product.price || 0),
			items: [toGaItem(product, 1)],
		});
	}, [product]);

	useEffect(() => {
		const fetchProductAndRelated = async () => {
			setLoading(true);
			setNotFound(false);
			setProduct(null);
			setSelectedVariant(null);
			setSelectedImage(null);
			setRelatedProducts([]);
			setLiveVisitors(0);
			setQuantity(1);

			try {
				const [foundProduct, relatedData] = await Promise.all([
					api.getProductBySlug(slug),
					api.getSuggestedProducts(slug),
				]);

				console.log(foundProduct);
				console.log("has_variants =", foundProduct.has_variants);
				console.log("variants =", foundProduct.variants);

				if (!foundProduct) {
					setNotFound(true);
					return;
				}

				setProduct({
					...foundProduct,
					views_count: foundProduct.views_count || 0,
				});

				setSelectedImage(
					foundProduct.images?.[0]?.image_url || foundProduct.image_url || null,
				);

				setRelatedProducts(relatedData || []);

				if (channelRef.current) {
					await supabase.removeChannel(channelRef.current);
					channelRef.current = null;
				}

				const channel = supabase.channel(`product-live-${foundProduct.id}`, {
					config: { presence: { key: crypto.randomUUID() } },
				});

				channelRef.current = channel;

				channel
					.on("presence", { event: "sync" }, () => {
						const state = channel.presenceState();
						setLiveVisitors(Object.keys(state).length);
					})
					.subscribe(async (status) => {
						if (status === "SUBSCRIBED") {
							await channel.track({
								online_at: new Date().toISOString(),
								slug: foundProduct.slug,
							});
						}
					});
			} catch (err) {
				console.error(err);
				setNotFound(true);
			} finally {
				setLoading(false);
			}
		};

		fetchProductAndRelated();

		return () => {
			if (channelRef.current) {
				supabase.removeChannel(channelRef.current);
				channelRef.current = null;
			}
		};
	}, [slug]);

	// Quand la variante change, on remet la quantité à 1
	useEffect(() => {
		setQuantity(1);
	}, [selectedVariant?.id]);

	const formatPrice = (price) =>
		new Intl.NumberFormat("fr-CH", {
			style: "currency",
			currency: "CHF",
		}).format(price);

	const getStockStatus = (stock) => {
		const safeStock = typeof stock === "number" ? stock : 0;
		if (safeStock <= 0)
			return { label: "Hors stock", className: "text-red-500" };
		if (safeStock <= 10)
			return { label: "Bientôt épuisé", className: "text-orange-500" };
		return { label: "✓ En stock", className: "text-green-600" };
	};

	const handleVariantSelect = (variant) => {
		setSelectedVariant(variant);

		// Si la variante possède plusieurs images,
		// on affiche sa première image
		if (variant.images?.length > 0) {
			setSelectedImage(variant.images[0].image_url);
			return;
		}

		// Sinon, si elle possède au moins son image principale
		if (variant.image_url) {
			setSelectedImage(variant.image_url);
			return;
		}

		// Sinon retour à l'image principale du produit
		setSelectedImage(
			product.images?.[0]?.image_url || product.image_url || null,
		);
	};

	const handleAddToCart = () => {
		if (!product) return;

		if (needsVariantSelection) {
			toast.error("Choisissez d'abord une option");
			return;
		}

		if (effectiveStock <= 0) {
			toast.error("Ce produit est hors stock");
			return;
		}
		if (quantity > effectiveStock) {
			toast.error(
				`Quantité indisponible. Il reste ${effectiveStock} article(s) en stock.`,
			);
			return;
		}

		// addItem accepte un 3e argument variant (peut être null pour produits simples)
		addItem(product, quantity, selectedVariant);

		trackEvent("add_to_cart", {
			currency: "CHF",
			value: Number(displayedPrice) * Number(quantity || 1),
			items: [
				toGaItem(
					{
						...product,
						price: displayedPrice,
						name: selectedVariant
							? `${product.name} — ${selectedVariant.name}`
							: product.name,
					},
					quantity || 1,
				),
			],
		});

		const productLabel = selectedVariant
			? `${product.name} — ${selectedVariant.name}`
			: product.name;

		toast.success(`${productLabel} ajouté au panier`, {
			description: `${quantity} × ${formatPrice(displayedPrice)}`,
			duration: 2000,
		});
	};

	const categorySlug = product?.category_slug || product?.category;
	const getCategoryLabel = (slug) => CATEGORY_LABELS[slug] || slug || "";

	const getDisplayViews = (views) =>
		!views || views === 0 ? Math.floor(Math.random() * 101) : views;

	if (loading || !product) {
		return (
			<main className="min-h-screen pt-20 bg-warm-alabaster">
				<div className="max-w-7xl mx-auto px-6 lg:px-12 py-6">
					<div className="w-20 h-4 bg-warm-secondary animate-pulse rounded mb-8" />
				</div>
				<section className="max-w-7xl mx-auto px-6 lg:px-12 pb-16">
					<div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
						<div className="aspect-[3/4] bg-warm-secondary animate-pulse" />
						<div className="flex flex-col gap-4 pt-4">
							<div className="h-3 bg-warm-secondary rounded w-1/4 animate-pulse" />
							<div className="h-8 bg-warm-secondary rounded w-3/4 animate-pulse" />
							<div className="h-6 bg-warm-secondary rounded w-1/4 animate-pulse" />
							<div className="h-3 bg-warm-secondary rounded w-full animate-pulse" />
							<div className="h-3 bg-warm-secondary rounded w-5/6 animate-pulse" />
							<div className="grid grid-cols-2 gap-4 mt-4">
								<div className="h-24 bg-warm-secondary rounded animate-pulse" />
								<div className="h-24 bg-warm-secondary rounded animate-pulse" />
							</div>
						</div>
					</div>
				</section>
			</main>
		);
	}

	if (notFound) {
		return (
			<main className="min-h-screen pt-20 flex items-center justify-center">
				<Helmet>
					<title>Produit introuvable | Léger's Coffee</title>
					<meta name="robots" content="noindex" />
				</Helmet>
				<div className="text-center">
					<h1 className="font-serif text-2xl text-espresso mb-4">
						Produit non trouvé
					</h1>
					<Button asChild className="btn-secondary">
						<Link to="/menu">Retour au Menu</Link>
					</Button>
				</div>
			</main>
		);
	}

	const stockStatus = getStockStatus(effectiveStock);
	const isOutOfStock = effectiveStock <= 0;
	const categoryInfo = CATEGORY_INFO[categorySlug] || null;

	const pageTitle = `${product?.name || "Produit"} | Léger's Coffee`;
	const pageDescription =
		product.description?.slice(0, 155) ||
		`Découvrez ${product.name} chez Léger's Coffee.`;
	const canonicalUrl = `https://legers-coffee.com/produit/${product?.slug || ""}`;

	return (
		<>
			<Helmet>
				<title>{pageTitle}</title>
				<meta name="description" content={pageDescription} />
				<link rel="canonical" href={canonicalUrl} />
				<meta
					name="robots"
					content={product.available ? "index, follow" : "noindex, follow"}
				/>
				<meta property="og:type" content="product" />
				<meta property="og:site_name" content="Léger's Coffee" />
				<meta property="og:title" content={pageTitle} />
				<meta property="og:description" content={pageDescription} />
				<meta property="og:url" content={canonicalUrl} />
				{product.image_url && (
					<meta property="og:image" content={product.image_url} />
				)}
				<meta name="twitter:card" content="summary_large_image" />
				<meta name="twitter:title" content={pageTitle} />
				<meta name="twitter:description" content={pageDescription} />
				{product.image_url && (
					<meta name="twitter:image" content={product.image_url} />
				)}
				<script type="application/ld+json">
					{JSON.stringify({
						"@context": "https://schema.org",
						"@type": "Product",
						name: product.name,
						description: pageDescription,
						image: product.image_url ? [product.image_url] : [],
						category: getCategoryLabel(categorySlug),
						offers: {
							"@type": "Offer",
							price: displayedPrice,
							priceCurrency: "CHF",
							availability: !isOutOfStock
								? "https://schema.org/InStock"
								: "https://schema.org/OutOfStock",
							url: canonicalUrl,
						},
					})}
				</script>
			</Helmet>

			<main
				data-testid="product-page"
				className="min-h-screen pt-20 bg-warm-alabaster"
			>
				<div className="max-w-7xl mx-auto px-6 lg:px-12 py-6">
					<button
						onClick={() => navigate(-1)}
						className="flex items-center text-soft-charcoal-secondary hover:text-espresso transition-colors duration-300"
						data-testid="back-button"
					>
						<ArrowLeft className="w-4 h-4 mr-2" />
						Retour
					</button>
				</div>

				<section className="max-w-7xl mx-auto px-6 lg:px-12 pb-16 lg:pb-24">
					<div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
						{/* Galerie produit */}
						<div className="relative" key={product.id}>
							{/* Grande image */}
							<div className="relative w-full aspect-[3/4] overflow-hidden flex items-center justify-center">
								{selectedImage || product.image_url ? (
									<div className="absolute inset-0">
										{displayedImages.length > 0 ? (
											displayedImages.map((image, index) => {
												const isActive =
													(selectedImage || product.image_url) ===
													image.image_url;

												return (
													<img
														key={image.id || index}
														src={image.image_url}
														alt={`${product.name} - vue ${index + 1}`}
														className={`absolute inset-0 block w-full h-full object-contain transition-opacity duration-700 ease-in-out ${
															isActive ? "opacity-100" : "opacity-0"
														}`}
														loading={index === 0 ? "eager" : "lazy"}
													/>
												);
											})
										) : (
											<img
												src={selectedImage || product.image_url}
												alt={product.name}
												className="block w-full h-full object-contain"
											/>
										)}
									</div>
								) : (
									<div className="w-full h-full flex items-center justify-center text-soft-charcoal-secondary/40 text-sm uppercase tracking-widest">
										{product.name}
									</div>
								)}

								{/* Badge vedette */}
								{product.featured && (
									<span className="absolute top-6 left-6 bg-latte-gold text-warm-alabaster px-4 py-2 text-xs font-medium uppercase tracking-wider">
										Vedette
									</span>
								)}

								{/* Badge nouveau */}
								{product.tags?.includes("nouveau") && !product.featured && (
									<span className="absolute top-6 left-6 bg-espresso text-warm-alabaster px-4 py-2 text-xs font-medium uppercase tracking-wider">
										Nouveau
									</span>
								)}
							</div>

							{/* Miniatures */}
							{displayedImages.length > 1 && (
								<div className="grid grid-cols-4 gap-3 mt-4">
									{displayedImages.map((image, index) => {
										const imageUrl = image.image_url;

										const isSelected =
											(selectedImage || product.image_url) === imageUrl;

										return (
											<button
												key={image.id || `${imageUrl}-${index}`}
												type="button"
												onClick={() => setSelectedImage(imageUrl)}
												className={`relative w-full aspect-square overflow-hidden border-2 transition-all duration-200 flex items-center justify-center ${
													isSelected
														? "border-latte-gold"
														: "border-transparent hover:border-latte-gold/40"
												}`}
												aria-label={`Afficher l'image ${index + 1} de ${product.name}`}
											>
												<img
													src={imageUrl}
													alt={`${product.name} - vue ${index + 1}`}
													className="block max-w-full max-h-full w-auto h-auto object-contain"
												/>

												{isSelected && (
													<div className="absolute inset-0 ring-1 ring-inset ring-latte-gold pointer-events-none" />
												)}
											</button>
										);
									})}
								</div>
							)}
						</div>

						{/* Infos */}
						<div className="flex flex-col justify-start pt-4">
							<span className="text-latte-gold text-sm uppercase tracking-widest font-medium mb-4">
								{getCategoryLabel(categorySlug)}
							</span>

							<h1 className="font-serif text-3xl lg:text-4xl text-espresso font-semibold mb-4">
								{product.name}
							</h1>

							<p className="text-2xl font-serif text-latte-gold mb-3">
								{hasVariants && !selectedVariant && (
									<span className="text-sm text-soft-charcoal-secondary uppercase tracking-wider font-sans font-normal mr-2">
										dès
									</span>
								)}
								{formatPrice(displayedPrice)}
							</p>

							{/* Vues + visiteurs live */}
							<div className="flex flex-wrap items-center gap-4 text-sm text-soft-charcoal-secondary mb-6">
								<div className="flex items-center gap-2">
									<Eye className="w-4 h-4" />
									<span>{getDisplayViews(product.views_count)} vues</span>
								</div>
								<div className="flex items-center gap-2">
									<Users className="w-4 h-4" />
									<span>
										{liveVisitors}{" "}
										{liveVisitors > 1
											? "personnes consultent"
											: "personne consulte"}{" "}
										ce produit
									</span>
								</div>
							</div>

							{/* Description */}
							<p className="text-soft-charcoal-secondary leading-relaxed mb-6">
								{product.description}
							</p>

							{/* Tags produit */}
							{product.tags && product.tags.length > 0 && (
								<div className="flex flex-wrap gap-2 mb-6">
									{product.tags.map((tag) => (
										<span
											key={tag}
											className="px-3 py-1 text-xs uppercase tracking-wider border border-border-subtle text-soft-charcoal-secondary"
										>
											{tag}
										</span>
									))}
								</div>
							)}

							{/* ── Sélecteur de variantes ── */}
							{hasVariants && (
								<VariantSelector
									variants={variants}
									selectedVariant={selectedVariant}
									onSelect={handleVariantSelect}
									formatPrice={formatPrice}
								/>
							)}

							{/* ── Infos contextuelles par catégorie ── */}
							{categoryInfo && (
								<>
									<Badges badges={categoryInfo.badges} />
									<InfoBlocks
										origin={categoryInfo.origin}
										process={categoryInfo.process}
									/>
									<StoryBlock story={categoryInfo.story} />
									<Assurances items={categoryInfo.assurance} />
								</>
							)}

							{/* Stock */}
							<p
								className={`${stockStatus.className} text-sm font-medium mb-6`}
							>
								{stockStatus.label}
							</p>

							{/* Ajout panier */}
							{!isOutOfStock && (
								<div className="space-y-4">
									<div className="flex items-center space-x-4">
										<span className="text-soft-charcoal-secondary">
											Quantité
										</span>
										<div className="flex items-center border border-border-subtle">
											<button
												type="button"
												onClick={() => setQuantity(Math.max(1, quantity - 1))}
												data-testid="decrease-quantity"
												className="w-12 h-12 flex items-center justify-center hover:bg-warm-secondary transition-colors duration-300"
											>
												<Minus className="w-4 h-4" />
											</button>
											<span className="w-12 text-center font-medium">
												{quantity}
											</span>
											<button
												type="button"
												onClick={() =>
													setQuantity((prev) =>
														Math.min(effectiveStock, prev + 1),
													)
												}
												data-testid="increase-quantity"
												className="w-12 h-12 flex items-center justify-center hover:bg-warm-secondary transition-colors duration-300"
											>
												<Plus className="w-4 h-4" />
											</button>
										</div>
									</div>

									<div className="flex flex-col sm:flex-row gap-4">
										<Button
											onClick={handleAddToCart}
											data-testid="add-to-cart-btn"
											disabled={needsVariantSelection}
											className="btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
										>
											<ShoppingBag className="w-5 h-5 mr-2" />
											{needsVariantSelection
												? "Choisissez une option"
												: "Ajouter au Panier"}
										</Button>
									</div>
								</div>
							)}
						</div>
					</div>
				</section>

				{/* Vous aimerez aussi */}
				{relatedProducts.length > 0 && (
					<section className="bg-warm-secondary py-16 lg:py-24">
						<div className="max-w-7xl mx-auto px-6 lg:px-12">
							<h2 className="font-serif text-2xl lg:text-3xl text-espresso font-semibold mb-12">
								Vous aimerez aussi
							</h2>
							<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-12">
								{relatedProducts.map((relatedProduct, index) => (
									<ProductCard
										key={relatedProduct.id}
										product={relatedProduct}
										index={index}
									/>
								))}
							</div>
						</div>
					</section>
				)}
			</main>
		</>
	);
}
